import { AssetChain, CHAIN_ID, SupportedTokenName } from "@mezo/sdk"
import { createSelector } from "@reduxjs/toolkit"

import { ETHEREUM_NETWORK, MEZO_CHAIN } from "#/shared/constants"
import { WalletBalances } from "#/shared/types"

import { RootState } from "../reducers"

export const selectIsConnected = (state: RootState) => state.wallet.isConnected

export const selectChainId = (state: RootState) => state.wallet.chainId

export const selectBitcoinAddress = (state: RootState) =>
  state.wallet.btcAddress

export const selectEvmAddress = (state: RootState) => state.wallet.evmAddress

export const selectIsBitcoinWallet = (state: RootState) =>
  !!state.wallet.btcAddress

export const selectHasExpectedChain = createSelector(
  (_, chainId: number) => chainId,
  (state: RootState) => state.wallet.chainId,
  selectIsConnected,
  (expectedChainId, walletChainId, isConnected) => {
    if (!walletChainId || !isConnected) return false
    return walletChainId === expectedChainId
  },
)
export const selectIsOnEthereumChain = (state: RootState) =>
  selectHasExpectedChain(state, CHAIN_ID[ETHEREUM_NETWORK])

export const selectIsOnMezoChain = (state: RootState) =>
  selectHasExpectedChain(state, MEZO_CHAIN.id)

export const selectWalletAddress = (state: RootState) => state.wallet.address
export const selectWalletBalances = (state: RootState) => state.wallet.balances
export const selectTokenAllowances = (state: RootState) =>
  state.wallet.tokenAllowance

export const selectWalletName = (state: RootState) =>
  state.wallet.name ?? state.wallet.address

export const selectTokenBalance = createSelector(
  [selectWalletBalances, (_, token: SupportedTokenName) => token],
  (balances, token) => balances[token].balance ?? 0,
)

export const selectTokenDecimals = createSelector(
  [selectWalletBalances, (_, token: SupportedTokenName) => token],
  (balances, token) => Number(balances[token].decimals) ?? 0,
)

export const selectWalletTokens = createSelector(
  selectWalletBalances,
  (tokens) => Object.keys(tokens) as SupportedTokenName[],
)

export const selectTokenAllowance = createSelector(
  [selectTokenAllowances, (_, token: SupportedTokenName) => token],
  (allowances, token) => allowances[token] ?? 0n,
)

export const selectMezoWalletBalances = createSelector(
  selectWalletBalances,
  (walletBalances) => {
    const tokenData = Object.entries(walletBalances)

    const mezoTokens = tokenData.filter(
      ([_, data]) => data.chain === AssetChain.MEZO,
    )

    return Object.fromEntries(mezoTokens) as Partial<WalletBalances>
  },
)

export const selectMezoTokens = createSelector(
  selectMezoWalletBalances,
  (token) => Object.keys(token) as SupportedTokenName[],
)

export const selectShouldPromptBeforeUnload = (state: RootState) =>
  state.wallet.isProcessingTransaction

export const selectDepositFromAnotherWallet = (state: RootState) =>
  state.wallet.depositFromAnotherWallet

export const selectHasLoadedWalletBalances = (state: RootState) =>
  state.wallet.hasLoadedWalletBalances
